import Image from 'next/image';
import classnames from 'classnames';

import { useSliderState } from '@/components/Slider/context';
import Authors from '@/components/Authors';
import Paragraph from '@/components/renderers/Paragraph';
import Button from '@/components/Button';
import QuoteSVG from '@/svg/icons/quote.svg';

export type Testimonial = {
  company: {
    caseStudy: {
      slug: string;
    };
    name: string;
    url: string;
    logo: {
      width: number;
      height: number;
      url: string;
      alt: string;
    };
  };
  author: {
    name: string;
    title: string;
    bio: string;
    photo: {
      width: number;
      height: number;
      url: string;
      alt: string;
    };
  };
};

export default function SliderTestimonial() {
  const { activeIndex, items } = useSliderState<Testimonial>();

  const containerWidth = `${items.length * 100}%`;

  const translateValue = () => {
    const width = 100 / items.length;

    return `-${activeIndex * width}%`;
  };

  return (
    <>
      <div className="hidden lg:block bg-white w-full max-w-[440px] isolate rounded-l-lg">
        <ul className="relative lg:h-[440px]">
          {items.map((item, index) => {
            const isActive = activeIndex === index;

            return (
              <li
                className={classnames(
                  `absolute bottom-0 duration-300 h-full left-0 right-0 top-0 transition-opacity w-full`,
                  {
                    'opacity-0 z-0': !isActive,
                    'opacity-100 z-10': isActive,
                  },
                )}
                key={item.author.photo.url}
              >
                <div className="absolute top-6 lg:ml-2 xl:-ml-10 z-50">
                  <Image
                    src={item.company.logo.url}
                    title={item.company.name}
                    width={128}
                    height={128}
                    objectFit="contain"
                    objectPosition="top"
                    alt={item.company.name}
                  />
                </div>
                <div className="absolute lg:left-2 xl:-left-10 z-50 bottom-6 bg-white px-5 py-4 shadow-elevation rounded">
                  <div className="text-sm md:text-base text-haiti">
                    {item.author.name}
                    {item.author.title && (
                      <span className="text-light-haiti text-sm block">
                        {item.author.title}
                      </span>
                    )}
                  </div>
                </div>
                <Image
                  src={item.author.photo.url}
                  title={item.author.name}
                  width={440}
                  height={440}
                  layout="responsive"
                  objectFit="cover"
                  className="!z-[-1] rounded-l-lg"
                  alt={item.author.name}
                />
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-col overflow-hidden w-full rounded-lg">
        <ul
          className="duration-300 flex flex-1 transition-transform"
          style={{
            width: containerWidth,
            transform: `translateX(${translateValue()})`,
          }}
        >
          {items.map((item, index) => (
            <li className="flex flex-1 justify-end bg-white" key={index}>
              <div className="flex flex-col justify-start lg:justify-center items-center lg:py-0 p-6 lg:px-8 lg:max-w-xl">
                <div className="lg:p-6 relative z-20 text-light-haiti">
                  <blockquote className="mb-6">
                    <div className="absolute pin-t pin-l lg:-mt-10 -ml-2">
                      <QuoteSVG className="text-primary-100 w-8 lg:w-20 opacity-70" />
                    </div>

                    <div className="relative lg:-mt-10">
                      <Paragraph className="text-base lg:text-lg">
                        {item.author.bio}
                      </Paragraph>
                    </div>
                  </blockquote>

                  <div className="-ml-3 mb-6 flex items-center justify-between lg:hidden">
                    <div className="-ml-px flex-grow">
                      <Authors people={[item.author]} showTitle />
                    </div>
                  </div>

                  {item.company.caseStudy && (
                    <Button
                      href={`/case-studies/${item.company.caseStudy.slug}`}
                      appearance="minimal"
                      colour="purple"
                      withArrow
                      className="px-0"
                    >
                      Read Case Study
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
